import { SagaReturnType } from 'redux-saga/effects';
import { call, delay, race, SagaGenerator } from 'typed-redux-saga';

import { RetryStrategy } from '../retry-strategy/types';
import { RetryWithTimeoutError, UnexpectedError } from './errors';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface RetryWithTimeoutParams<Fn extends (...args: any[]) => any> {
    maxTries: number;
    timeout?: number;
    beforeRun?: (attempt: number) => Maybe<SagaGenerator<void>>;
    onSuccess?: () => void;
    onAttemptError?: (error: unknown, attempt: number) => SagaGenerator<Maybe<boolean>> | undefined | boolean;
    getArgs?: (attempt: number) => SagaGenerator<Parameters<Fn>>;
    retryStrategy?: RetryStrategy;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* retryWithTimeout<Ctx, Fn extends (this: Ctx, ...args: any[]) => any>(
    this: Ctx,
    params: RetryWithTimeoutParams<Fn>,
    fn: Fn,
    ...args: Parameters<Fn>
): SagaGenerator<unknown> {
    const { maxTries, timeout, getArgs, onAttemptError, beforeRun, onSuccess } = params;
    const retryStrategy = params.retryStrategy ?? null;

    let attempt = 0;

    while (attempt < maxTries) {
        try {
            if (retryStrategy) {
                yield* call([retryStrategy, retryStrategy.nextAttempt]);
            }

            if (getArgs) {
                args = yield* call([this, getArgs], attempt) || args;
            }

            if (beforeRun) {
                yield* call(beforeRun, attempt);
            }

            const { result, timedOut } = yield* call([
                this,
                function* (): SagaGenerator<{ result?: unknown; timedOut?: true }> {
                    if (typeof timeout === 'number') {
                        const { result, timedOut } = yield* race({
                            result: call([this, fn], ...args),
                            timedOut: delay(timeout),
                        });
                        return {
                            result: result,
                            timedOut: timedOut,
                        };
                    }
                    return {
                        result: yield* call([this, fn], ...args),
                    };
                },
            ]);

            if (timedOut) {
                throw new RetryWithTimeoutError();
            }

            onSuccess?.();

            return result as SagaReturnType<Fn>;
        } catch (error) {
            const shouldStopAttempts = yield* call(function* () {
                if (onAttemptError) {
                    return yield* call(onAttemptError, error, attempt);
                }
                return false;
            });

            attempt++;

            if (attempt >= maxTries || shouldStopAttempts) {
                throw error;
            }
        }
    }

    // Не должно дойти до сюда, но нужно для корректной работы типов.
    throw new UnexpectedError();
}
